
import { Component, Ref, Vue } from 'vue-property-decorator'
import LearningProblem from '@/components/templates/study/LearningProblem.vue'
import { StudyModel } from '@/models/StudyModel'
import { CurriculumUnit } from '@/models/CurriculumUnit'
import { FixedExercisesApi } from '@/api/methods/FixedExercisesApi'
import { CurriculumUnitsApi } from '@/api/methods/CurriculumUnitsApi'
import { ProblemsApi } from '@/api/methods/ProblemsApi'
import { StudyGeneralLearningsApi } from '@/api/methods/study/StudyGeneralLearningsApi'
import { StudyGeneralLearningMemosApi } from '@/api/methods/study/StudyGeneralLearningMemosApi'
import { ServerTimesApi } from '@/api/methods/ServerTimesApi'
import StrokeUtils from '@/utils/StrokeUtils'

/**
 * 小問指定で演習を実施(通常学習)
 */
@Component({
  components: {
    LearningProblem,
  },
})
export default class GeneralProblem extends Vue {
  /** テンプレート要素 */
  @Ref()
  learningProblem!: LearningProblem

  /** 読み込み完了フラグ */
  private isLoaded = false

  /** 単元情報 */
  private curriculumUnit!: CurriculumUnit

  /** 小問情報 */
  private problem?: StudyModel.Problem

  /** 演習開始時刻(UNIX秒) */
  private startedTimestamp: number = 0

  /**
   * 固定問題ID
   *
   * @returns 固定問題ID
   */
  private get fixedExerciseId(): number {
    return Number(this.$route.params.fixedExerciseId)
  }

  /**
   * 小問ID
   *
   * @returns 小問ID
   */
  private get problemId(): number {
    return Number(this.$route.params.problemId)
  }

  /**
   * クエリ保持の教材ID
   *
   * @returns 教材ID
   */
  private get studyCategoryId(): number {
    return Number(this.$route.query.studyCategoryId)
  }

  /**
   * クエリ保持の系統図ID
   *
   * @returns 系統図ID
   */
  private get systemChartId(): number {
    return Number(this.$route.query.systemChartId)
  }

  /**
   * 初回処理
   */
  private async mounted(): Promise<void> {
    this.$loading.start()

    // 固定問題情報取得
    const fixedExercise = await FixedExercisesApi.getFixedExercise(this.fixedExerciseId)

    // 単元情報取得
    const curriculumUnit = await CurriculumUnitsApi.getGeneral(fixedExercise.curriculumUnitId)
    this.curriculumUnit = {
      id: curriculumUnit.id,
      name: curriculumUnit.name,
    }

    // 小問情報取得
    const problem = await ProblemsApi.getProblem(this.problemId)

    // 小問情報を生成
    this.problem = new StudyModel.Problem(problem.code, problem.imageDescriptions, [])

    // 設問情報を生成
    problem.questions.forEach((question) => {
      // undefinedブロック(実際には発生しない)
      if (!this.problem) {
        return
      }

      // 設問情報を追加
      this.problem.addQuestionByResponse(question)
    })

    // 演習開始時刻の取得
    const time = await ServerTimesApi.getTime()
    this.startedTimestamp = time.unixTime

    this.isLoaded = true

    this.$loading.complete()
  }

  /**
   * 答え合わせボタン押下時処理
   */
  private async eventSubmit(): Promise<void> {
    // undefinedブロック(実際には発生しない)
    if (!this.problem) {
      return
    }

    this.$loading.start()

    // 演習結果登録
    const resultLearning = await StudyGeneralLearningsApi.postStudyGeneralLearning(
      this.fixedExerciseId,
      this.problemId,
      this.problem.inputValues(),
      this.startedTimestamp
    )

    // 演習メモ登録
    await this.postLearningMemo(resultLearning.learningId)

    this.$loading.complete()

    // 例題の場合は戻るボタン押下時と同じ画面に遷移
    if (this.problem.isExample) {
      await this.eventReturn()
      return
    }

    // クエリのセット
    let query = {}
    if (this.studyCategoryId) {
      // 教材ID
      query = { studyCategoryId: `${this.studyCategoryId}` }
    } else if (this.systemChartId) {
      // 系統図ID
      query = { systemChartId: `${this.systemChartId}` }
    }

    await this.$router.push({
      path: `/student/general/result/${resultLearning.learningId}`,
      query: query,
    })
  }

  /**
   * 演習メモの登録
   *
   * @param learningId 演習ID
   */
  private async postLearningMemo(learningId: number): Promise<void> {
    // メモが存在しない場合は終了
    const memoStrokes = this.learningProblem.memoArea.strokes
    if (memoStrokes.length <= 0) {
      return
    }

    // ストローク情報の変換
    const strokes = StrokeUtils.strokesToCompressedJson(memoStrokes)

    // 登録API実行
    try {
      await StudyGeneralLearningMemosApi.postGeneralLearningMemo(learningId, strokes)
    } catch {
      // 例外発生時は無視して握りつぶす
    }
  }

  /**
   * 戻るボタン押下時処理
   */
  private async eventReturn(): Promise<void> {
    // クエリのセット
    let query = {}
    if (this.studyCategoryId) {
      // 教材ID
      query = { studyCategoryId: `${this.studyCategoryId}` }
    } else if (this.systemChartId) {
      // 系統図ID
      query = { systemChartId: `${this.systemChartId}` }
    }

    await this.$router.push({
      path: `/student/general/${this.curriculumUnit.id}`,
      query: query,
    })
  }

  /**
   * トップページボタン押下時処理
   */
  private async eventTopPage(): Promise<void> {
    await this.$router.push('/student/top')
  }
}
